import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./style";
import color from "../../../../../constants/color";
import { formatDate } from "../../../../../utils/DateFormat";

const AllTransactionList = ({
  data = [],
  vendors,
  customers,
  startDate,
  setTotalSelectedDeposits,
  setTotalSelectedWithdrawals,
  selected,
  setSelected,
  lastClosingBalance,
}) => {
  const navigate = useNavigate();

  const getId = (row, idx) => row._id ?? row.id ?? idx;

  const handleToggle = useCallback(
    (id) => (e) => {
      e.stopPropagation();
      setSelected((prev) =>
        prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
      );
    },
    []
  );

  const handleToggleAll = (e) => {
    e.stopPropagation();
    const pageIds = data.map(getId);
    const allOnPage = pageIds.every((id) => selected.includes(id));

    setSelected((prev) =>
      allOnPage
        ? prev.filter((id) => !pageIds.includes(id))
        : [...prev, ...pageIds.filter((id) => !prev.includes(id))]
    );
  };

  const allChecked =
    data.length > 0 && data.every((r, i) => selected.includes(getId(r, i)));
  const indeterminate = selected.length > 0 && !allChecked;

  const vendorMap = useMemo(() => {
    const map = {};
    vendors.forEach((v) => {
      map[v._id] = v.name;
    });
    return map;
  }, [vendors]);

  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      map[c._id] = c.name;
    });
    return map;
  }, [customers]);

  // Store selected transactions and their totals for reuse
  const selectedTransactions = useMemo(
    () => data.filter((item, idx) => selected.includes(getId(item, idx))),
    [data, selected]
  );

  const totalSelectedDeposits = useMemo(
    () =>
      selectedTransactions.reduce(
        (sum, item) => sum + (Number(item.deposit) || 0),
        0
      ),
    [selectedTransactions]
  );

  const totalSelectedWithdrawals = useMemo(
    () =>
      selectedTransactions.reduce(
        (sum, item) => sum + (Number(item.withdrawal) || 0),
        0
      ),
    [selectedTransactions]
  );

  useEffect(() => {
    setTotalSelectedDeposits(totalSelectedDeposits);
    setTotalSelectedWithdrawals(totalSelectedWithdrawals);
  }, [totalSelectedDeposits, totalSelectedWithdrawals]);

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.TableHead}>
                <Checkbox
                  sx={{ p: 0, m: 0 }}
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onClick={handleToggleAll}
                />
              </TableCell>
              <TableCell sx={styles.TableHead}>Date</TableCell>
              <TableCell sx={styles.TableHead}>Description</TableCell>
              <TableCell sx={styles.TableHead}>Type</TableCell>
              <TableCell sx={styles.TableHead} align="right">
                Deposits
              </TableCell>
              <TableCell sx={styles.TableHead} align="right">
                Withdrawals
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="h6">No data</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                <TableRow>
                  <TableCell sx={styles.tableData}></TableCell>
                  <TableCell sx={styles.tableData}>
                    {startDate
                      ? formatDate(startDate)
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...styles.tableData,
                      maxWidth: 200,
                      minWidth: 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={"*** Opening Balance ***"}
                  >
                    *** Opening Balance ***
                  </TableCell>
                  <TableCell sx={styles.tableData}></TableCell>
                  <TableCell sx={styles.tableData} align="right">
                    {`$${lastClosingBalance.toLocaleString()}`}
                  </TableCell>
                  <TableCell sx={styles.tableData} align="right"></TableCell>
                </TableRow>
                {data.map((item, idx) => {
                  const rowId = getId(item, idx);
                  const vendorName = item.vendor
                    ? vendorMap[item.vendor._id || item.vendor] ||
                      item.vendor.name ||
                      item.vendor
                    : "";
                  const customerName = item.customer
                    ? customerMap[item.customer._id || item.customer] ||
                      item.customer.name ||
                      item.customer
                    : "";
                  return (
                    <TableRow key={rowId} hover sx={{ cursor: "pointer" }}>
                      <TableCell sx={styles.tableData}>
                        <Checkbox
                          sx={{ p: 0, m: 0 }}
                          checked={selected.includes(rowId)}
                          onClick={handleToggle(rowId)}
                        />
                      </TableCell>
                      <TableCell sx={styles.tableData}>
                        {item?.date
                          ? formatDate(item.date)
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...styles.tableData,
                          maxWidth: 200,
                          minWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.description}
                      >
                        {item.description}
                      </TableCell>
                      <TableCell sx={styles.tableData}>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "13px",
                              color: color.text,
                            }}
                          >
                            {item.vendor
                              ? "Vendor Payment"
                              : "Customer Payment"}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "12px", color: color.lightText }}
                          >
                            {item.vendor
                              ? `Vendor: ${vendorName}`
                              : item.customer
                              ? `Customer: ${customerName}`
                              : ""}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.tableData} align="right">
                        {item.deposit ? `$${item.deposit}` : ""}
                      </TableCell>
                      <TableCell sx={styles.tableData} align="right">
                        {item.withdrawal ? `$${item.withdrawal}` : ""}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                    TOTAL OF SELECTED TRANSACTIONS
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={styles.tableData} align="right">
                    {`$${totalSelectedDeposits.toFixed(2)}`}
                  </TableCell>
                  <TableCell sx={styles.tableData} align="right">
                    {`$${totalSelectedWithdrawals.toFixed(2)}`}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AllTransactionList;
